import React, { useContext, useEffect, useState } from "react";
import Grid from "@material-ui/core/Grid";
import useStyles from "./styles";
import order from "@api/order";
import { __t } from "@core/translation/translation";
import CustomTableWithPagination from "@components/CustomTableWithPagination";
import Typography from "@components/Typography";
import NumberFormat from "react-number-format";
import clsx from "clsx";
import {
  IconButton,
  Tooltip,
  InputAdornment,
  OutlinedInput,
} from "@material-ui/core";
import { notification } from "@core/helper/notificationHelper";
import { Paper, Popover } from "@material-ui/core";
import CustomizedSnackbars from "@components/SnackBar";
import { getMarketPairDisplay } from "@core/utils/getCurrencyMeta";
import { MarketContext } from "@context/MarketContext";
import { useRangerOptional } from "@context/rangerContext";
import { GetCurrencyScale, GetMarketScale } from "@core/helper/getScale";
import { formatDisplayDateTime } from "@core/helper/formatDisplayDateTime";

const MarketActiveOrders = ({
  pageSize,
  pageNumber,
  pagination,
  TableOptionOverride,
  className,
  emptySize = "default",
}) => {
  const classes = useStyles();
  const market = useContext(MarketContext);
  const ranger = useRangerOptional();
  const [state, setState] = useState({
    pageSize: pageSize,
    pageNumber: pageNumber,
    data: [],
    is_fetch: false,
    count: 0,
    open: false,
    anchorEl: null,
  });

  // WS4.3 — refresh open orders on private `order` events
  useEffect(() => {
    if (!ranger?.onPrivateEvent) return undefined;
    return ranger.onPrivateEvent("order", () => {
      market.setForceUpdate((v) => !v);
    });
  }, [ranger?.onPrivateEvent, market.setForceUpdate]);

  useEffect(() => {
    order
      .getListActive({
        params: {
          pageNumber: state.pageNumber,
          pageSize: state.pageSize,
        },
      })
      .then((data) => {
        market.setOrderLength(data.content.length);
        setState((prevState) => ({
          ...prevState,
          data: data.content,
          is_fetch: true,
          count: data.count,
        }));
      })
      .catch((data) => {
        console.log(data?.message);
      });
  }, [market.forceUpdate]);

  const handleChange = (event, page) => {
    setState((prevState) => ({
      ...prevState,
      is_fetch: false,
      pageNumber: page - 1,
    }));
  };
  const handlePopoverOpen = (event, popoverId) => {
    setState((prevState) => ({
      ...prevState,
      openedPopoverId: popoverId,
      anchorEl: event.target,
    }));
  };
  const handlePopoverClose = () => {
    setState((prevState) => ({
      ...prevState,
      openedPopoverId: null,
      anchorEl: null,
    }));
  };

  const handleDelete = (id) => {
    order
      .cancelOrderById({
        params: {
          id: id,
        },
      })
      .then((data) => {
        notification("", "success", data.message);
        market.setForceUpdate(!market.forceUpdate);
      })
      .catch((data) => {
        notification(
          "",
          "danger",
          !!data && !!data?.message ? data?.message : "SERVER ERROR"
        );
      });
  };

  const TableOption = [
    {
      name: "created",
      title: __t("time"),
      date: true,
      render: (record, status) => (
        <Typography variant="3xs" color="secondary.tint40">
          {formatDisplayDateTime(record.created, { pattern: "full", input: "iso" })}
        </Typography>
      ),
    },
    {
      name: "currencyName",
      title: __t("Market"),
      render: (record, status) => {
        const { baseName, quoteName } = getMarketPairDisplay(record?.marketName);
        return (
          <div className={classes.coinLogoAndText}>
            <Typography variant="3xs" isBold className={classes.currencyName}>
              {baseName}
            </Typography>
            <Typography variant="3xs" className={classes.seperator}>
              {"/"}
            </Typography>
            <Typography variant="3xs">{quoteName}</Typography>
          </div>
        );
      },
    },

    {
      name: "type",
      title: __t("Order type"),
      render: (record, status) => (
        <Typography variant="3xs" color="secondary.main">
          {__t(record.type)}
        </Typography>
      ),
    },
    {
      name: "side",
      title: __t("To order"),
      render: (record, status) => (
        <div className={classes.orderTypeWrapper}>
          {record.side === "SELL" ? (
            <span className={clsx("icon-arrow-up", classes.orderTypeIconUp)} />
          ) : (
            <span
              className={clsx("icon-arrow-down", classes.orderTypeIconDown)}
            />
          )}

          <Typography variant="3xs" color="secondary.main">
            {__t(record.side)}
          </Typography>
        </div>
      ),
    },

    {
      name: "unitPrice",
      title: __t("price (toman)"),
      render: (record, status) => (
        <Typography variant="3xs" color="secondary.main">
          <NumberFormat
            value={record.unitPrice}
            displayType={"text"}
            thousandSeparator={true}
          />
        </Typography>
      ),
    },
    {
      title: __t("Total Amount"),
      render: (record, status) => {
        return (
          <Typography
            variant="3xs"
            className={classes.currencyName}
            color="secondary.main"
          >
            {record.size}
          </Typography>
        );
      },
    },
    {
      title: __t("Completed Amount"),
      render: (record, status) => {
        return (
          <Typography variant="3xs" color="secondary.main">
            {record.size - record.remainedSize}
          </Typography>
        );
      },
    },
    {
      name: "AveragePrice",
      title: __t("Average price"),
      render: (record, status) => (
        <Typography variant="3xs" color="secondary.main">
          <NumberFormat
            value={record.unitPrice}
            displayType={"text"}
            thousandSeparator={true}
          />
        </Typography>
      ),
    },
    {
      name: "Total",
      title: __t("Toatal Amount(Toman)"),
      render: (record, status) => (
        <Typography variant="3xs" color="secondary.main">
          <NumberFormat
            value={Math.ceil(record.unitPrice * record.size)}
            displayType={"text"}
            thousandSeparator={true}
          />
        </Typography>
      ),
    },
    {
      name: "fee",
      title: __t("Fee"),
      render: (record, status) => {
        const { baseUnit, quoteUnit } = getMarketPairDisplay(record?.marketName);
        return (
          <Typography variant="3xs" className={classes.orderTypeWrapper}>
            <NumberFormat
              value={
                record.side === "SELL"
                  ? record.fee.toFixed(GetMarketScale(quoteUnit))
                  : record.fee.toFixed(GetCurrencyScale(baseUnit))
              }
              displayType={"text"}
              thousandSeparator={true}
            />
            <Typography
              variant="3xs"
              color="secondary.tint40"
              className={classes.seperator}
            >
              ({record.side === "SELL" ? baseUnit : quoteUnit})
            </Typography>
          </Typography>
        );
      },
    },

    {
      lastFixed: true,
      render: (record, status) => (
        <div className={classes.wrapperLastCol}>
          <Tooltip arrow title={__t("Terminate Order")} placement="top-center">
            <IconButton
              size="small"
              onClick={() => handleDelete(record.id)}
              className={classes.xBtn}
              color="primary"
            >
              <span className="icon-x" />
            </IconButton>
          </Tooltip>
          <Tooltip arrow title={__t("See details")} placement="top-center">
            <IconButton
              size="small"
              onClick={(e) => handlePopoverOpen(e, record.id)}
              className={classes.detailBtn}
              color="primary"
            >
              <span
                className={
                  state.openedPopoverId === record.id ? "icon-x" : "icon-eye"
                }
              />
            </IconButton>
          </Tooltip>
          {record.id === state.openedPopoverId && (
            <Popover
              open={state.openedPopoverId === record.id}
              anchorEl={state.anchorEl}
              onClose={handlePopoverClose}
              className={classes.popOverContainer}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <Paper className={classes.popOver} elevation={0}>
                <Grid container direction="column" spacing={10}>
                  <Grid
                    item
                    container
                    direction="row"
                    justify="space-between"
                    alignItems="center"
                  >
                    <Grid item>
                      <Typography variant="2xs">{__t("Order ID")}</Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="2xs" classes={classes.popOverText}>
                        2/5 کانفرم
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <OutlinedInput
                      name="code"
                      fullWidth
                      value="btc-irt6486"
                      disabled
                      className={classes.input}
                      endAdornment={
                        <InputAdornment variant="filled" position="end">
                          <CustomizedSnackbars
                            text="btc-irt6486"
                            message={__t("Copied successfully")}
                          />
                        </InputAdornment>
                      }
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Popover>
          )}
        </div>
      ),
    },
  ];
  return (
    <>
      <CustomTableWithPagination
        TableOption={TableOptionOverride ? TableOptionOverride : TableOption}
        className={className}
        data={state.data}
        is_fetch={state.is_fetch}
        handleChange={handleChange}
        pageNumber={state.pageNumber}
        count={state.count}
        pageSize={state.pageSize}
        pagination={pagination}
        type="sticky"
        emptySize={emptySize}
      />
    </>
  );
};

export default MarketActiveOrders;
